import { useCallback, useReducer } from 'react'
import effects from 'app/effects/effects'
import { EXTERNAL_GROUP, ORDINARY_GROUP } from 'commons/groupLogic'
import moment from 'moment'
import MessageAlert from 'app/components/MessageAlert'
import { useIntl } from 'react-intl'
import { useDispatch } from 'react-redux'
import { equals, isEmpty, mergeAll, reject } from 'ramda'
import debounce from 'lodash.debounce'
import { ACTION_NEW, GENERAL_DIALOG } from 'app/constants/compose'
import { openDialog } from 'app/auth/store/actions/createNotification.actions'
import { SCOPE_BEACON } from 'app/constants/scope'
import { Routes } from 'app/services/routeService/routeService'
import { isEmptyOrNil } from 'libs/empty'
import { useHistory } from 'react-router-dom'
import { useGroups } from 'commons/useGroups'
import { errorTypes } from 'commons/error'

let controller = new AbortController()

const actionTypoe = {
  START_FETCH: 'START_FETCH',
  FETCH_SUCCESS: 'FETCH_SUCCESS',
  FETCH_ERROR: 'FETCH_ERROR',
  DELETE_SUCCESS: 'DELETE_SUCCESS',
  CLOSE_CREATE_DIALOG: 'CLOSE_CREATE_DIALOG',
  OPEN_CREATE_DIALOG: 'OPEN_CREATE_DIALOG',
  SET_SEARCH_TEXT: 'SET_SEARCH_TEXT',
  REFETCH_DATA: 'REFETCH_DATA',
}

const initialState = {
  groups: [],
  groupCount: 0,
  meta: {},
  params: {},
  isLoading: false,
  isEmptyGroups: false,
  error: undefined,
  searchText: '',
}

const reducer = (state, action) => {
  switch (action.type) {
    case actionTypoe.START_FETCH:
      return {
        ...state,
        isLoading: true,
      }
    case actionTypoe.REFETCH_DATA:
      return {
        ...state,
        isLoading: true,
      }

    case actionTypoe.FETCH_SUCCESS:
      return {
        ...state,
        isLoading: false,
        groups: action.payload.data,
        groupCount: action.payload.groupCount,
        meta: action.payload.meta,
        params: action.params,
        isEmptyGroups:
          isEmptyOrNil(state.searchText) && action.payload.data.length === 0,
      }
    case actionTypoe.FETCH_ERROR:
      return {
        ...state,
        isLoading: false,
        groups: [],
        groupCount: 0,
        meta: undefined,
        error: action.payload,
      }

    case actionTypoe.DELETE_SUCCESS:
      return {
        ...state,
        isLoading: true,
      }

    case actionTypoe.SET_SEARCH_TEXT:
      return {
        ...state,
        searchText: action.payload,
      }

    default:
      return state
  }
}

export const mergeTagIds = (groups) => {
  return groups.flatMap((group) =>
    group.tagIds ? group.tagIds.split(',').map(Number) : [],
  )
}

export default function useGroup() {
  const [state, dispatch] = useReducer(reducer, initialState)
  const { formatMessage } = useIntl()
  const dispatchSystem = useDispatch()
  const history = useHistory()
  const { fetchAllGroups } = useGroups()

  const groupApi = (params) => {
    controller.abort()
    controller = new AbortController()
    effects.apis
      .groupsV4({
        placeId: params.placeId,
        limit: params?.limit || 20,
        type: `${ORDINARY_GROUP},${EXTERNAL_GROUP}`,
        page: params?.page || 1,
        sort: params?.sort,
        keyword: params?.keyword,
      })
      .then((respone) => {
        dispatch({
          type: actionTypoe.FETCH_SUCCESS,
          payload: respone,
          params,
        })
      })
      .catch((error) => {
        dispatch({ type: actionTypoe.FETCH_ERROR, payload: error })
      })
  }

  const refetchGroups = async (params) => {
    dispatch({ type: actionTypoe.REFETCH_DATA })
    groupApi(params)
    await fetchAllGroups()
  }

  const getGroupFirstTime = (params) => {
    dispatch({ type: actionTypoe.START_FETCH })
    groupApi(params)
  }

  const debounceFetchGroups = useCallback(
    debounce((value, onClearData) => {
      dispatch({ type: actionTypoe.START_FETCH })
      groupApi(value)
      onClearData()
    }, 1000),
    [],
  )

  const searchGroups = (params) => {
    const mapParams = reject(equals(''))(
      mergeAll([
        {
          placeId: params.placeId,
          keyword: params.keyword,
          page: 1,
          limit: state.meta.limit,
        },
      ]),
    )
    dispatch({ type: actionTypoe.SET_SEARCH_TEXT, payload: params.keyword })
    debounceFetchGroups(mapParams, params.onClearData)
  }

  const deleteGroupByIds = async ({ groupIds, placeId }) => {
    try {
      const response = await effects.apis.deleteGroups({ groupIds })
      dispatchSystem(
        MessageAlert.showSuccess({
          message: formatMessage({
            id: 'groupsv4.delete.dialog.deleted.message',
          }),
        }),
      )

      return response
    } catch (error) {
      dispatchSystem(
        MessageAlert.showError({
          message: formatMessage({ id: 'message.alert.error' }),
        }),
      )
      throw error
    }
  }

  const deleteSubgroupFromTag = async ({ subscriberIds, tagId }) => {
    try {
      const response = await effects.apis.detachSubscribers({
        subscriberIds,
        tagId,
      })
      dispatchSystem(
        MessageAlert.showSuccess({
          message: formatMessage({
            id: 'groupsv4.subscriber.remove.message',
          }),
        }),
      )

      return response
    } catch (error) {
      dispatchSystem(
        MessageAlert.showError({
          message: formatMessage({ id: 'message.alert.error' }),
        }),
      )
      throw error
    }
  }

  const deleteSubgroupFromGroup = async ({ subscriberIds, groupId }) => {
    try {
      const response = await effects.apis.detachSubscribersFromGroup({
        subscriberIds,
        groupId,
      })
      dispatchSystem(
        MessageAlert.showSuccess({
          message: formatMessage({
            id: 'groupsv4.subscriber.remove.message',
          }),
        }),
      )

      return response
    } catch (error) {
      dispatchSystem(
        MessageAlert.showError({
          message: formatMessage({ id: 'message.alert.error' }),
        }),
      )
      throw error
    }
  }

  const createGroups = async (params) => {
    try {
      const response = await effects.apis.createGroup(params)
      dispatchSystem(
        MessageAlert.showSuccess({
          message: formatMessage({
            id: 'groupsv4.cerateGroup.dialog.success.message',
          }),
        }),
      )

      return response
    } catch (error) {
      const code = error?.response?.data?.code
      if (code === 608) {
        dispatchSystem(
          MessageAlert.showError({
            message: formatMessage({
              id: 'duplicate.group.name.error.message',
            }),
          }),
        )
      } else {
        dispatchSystem(
          MessageAlert.showError({
            message: formatMessage({ id: 'message.alert.error' }),
          }),
        )
      }
      throw error
    }
  }

  const addSubgroup = async (params) => {
    try {
      const response = await effects.apis.addSubgroup(params)
      dispatchSystem(
        MessageAlert.showSuccess({
          message: formatMessage({
            id: 'groupsv4.subgroup.dialog.added',
          }),
        }),
      )
      return response
    } catch (error) {
      dispatchSystem(
        MessageAlert.showError({
          message: formatMessage({ id: 'message.alert.error' }),
        }),
      )
      throw error
    }
  }

  const editGroupName = async (params) => {
    try {
      const response = await effects.apis.updateGroup(params)
      dispatchSystem(
        MessageAlert.showSuccess({
          message: formatMessage({
            id: 'groupsv4.updateGroupName.dialog.success.message',
          }),
        }),
      )

      return response
    } catch (error) {
      dispatchSystem(
        MessageAlert.showError({
          message: formatMessage({ id: 'message.alert.error' }),
        }),
      )
      throw error
    }
  }

  const editSubGroupName = async (params) => {
    try {
      const response = await effects.apis.updateSubGroup(params)
      dispatchSystem(
        MessageAlert.showSuccess({
          message: formatMessage({
            id: 'groupsv4.editSubgroupName.dialog.success.message',
          }),
        }),
      )

      return response
    } catch (error) {
      dispatchSystem(
        MessageAlert.showError({
          message: formatMessage({ id: 'message.alert.error' }),
        }),
      )
      throw error
    }
  }

  const addSubscriberToTags = async (params) => {
    try {
      const response = await effects.apis.addSubscriberToTags(params)
      dispatchSystem(
        MessageAlert.showSuccess({
          message: formatMessage(
            {
              id: 'groupsv4.dialog.add.subscriber.member.success',
            },
            { count: params.subscriberIds.length },
          ),
        }),
      )

      return response
    } catch (error) {
      dispatchSystem(
        MessageAlert.showError({
          message: formatMessage({ id: 'message.alert.error' }),
        }),
      )
      throw error
    }
  }

  const deleteSubgroup = async (params) => {
    try {
      const response = await effects.apis.deleteSubgroup(params)
      dispatchSystem(
        MessageAlert.showSuccess({
          message: formatMessage({
            id: 'groupsv4.subgroup.deleted.message',
          }),
        }),
      )

      return response
    } catch (error) {
      dispatchSystem(
        MessageAlert.showNetworkError(formatMessage)(error, errorTypes.default),
      )
      throw error
    }
  }

  const onSuccseAndRedirectPage = (res, scope) => {
    dispatch(
      MessageAlert.showSuccess({
        message: formatMessage({
          id: 'createNotification.success.message',
        }),
      }),
    )
    setTimeout(() => {
      const pathname =
        scope === SCOPE_BEACON
          ? Routes.beaconNotification()
          : Routes.notificationTimeline()
      history.push(pathname)
    }, 2000)
  }

  const openCompose = (tagId) => {
    dispatchSystem(
      openDialog({
        initialValues: isEmpty(tagId)
          ? undefined
          : {
              subGroups: tagId,
            },
        onCreateSuccess: onSuccseAndRedirectPage,
        type: GENERAL_DIALOG,
        action: ACTION_NEW,
      }),
    )
  }

  const setSearchtext = (text) => {
    dispatch({ type: actionTypoe.SET_SEARCH_TEXT, payload: text })
  }

  const getTagIds = async ({ groupIds }) => {
    return await effects.apis.getTagIds({ groupIds })
  }

  return {
    state,
    refetchGroups,
    getGroupFirstTime,
    searchGroups,
    createGroups,
    deleteGroupByIds,
    openCompose,
    editGroupName,
    addSubgroup,
    setSearchtext,
    editSubGroupName,
    deleteSubgroup,
    deleteSubgroupFromGroup,
    deleteSubgroupFromTag,
    addSubscriberToTags,
    getTagIds,
  }
}
